/* eslint-disable no-param-reassign */
//  @flow

import type { ButtonProps } from 'components/atoms/button/Button';
import type { InputType } from 'components/atoms/input/react/InputType';

import classNames from 'classnames';
import Icon from 'components/_particles/icon/react/Icon';
import Button from 'components/atoms/button/Button';
import Input from 'components/atoms/input/react/Input';
import Label from 'components/atoms/label/react/Label';
import { useCombobox } from 'downshift';
import useDebounce from 'javascripts/hooks/useDebounce';
import { t } from 'javascripts/utils/withTranslation';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';

export type Props = {
  ...InputType,
  suggestUrl: string,
  suggestFieldName?: string,
  suggestionItems?: string[],
  suggestTitle?: string,
  label?: string,
  error?: string,
  formProps?: {
    action?: string,
    method?: string,
  },
  hideSubmit?: boolean,
  tag?: string,
  value?: string,
  submitButtonProps?: Partial<ButtonProps>,
};

export type SearchResult = {
  suggestions: {
    [key: string]: number,
  },
  suggestion: string,
};

const fetchAutocomplete = async (url: string): Promise<string[]> => {
  try {
    const response = await fetch(url);
    const data: SearchResult = await response.json();
    return Object.keys(data?.suggestions || []) || [];
  } catch (error) {
    console.error('Error fetching autocomplete suggestions:', error);
    return [];
  }
};

const Search: React.AbstractComponent<Props, HTMLInputElement> = forwardRef(
  (props: Props, ref: any) => {
    const {
      suggestUrl,
      suggestFieldName = 'tx_solr[queryString]',
      placeholder,
      name = 'tx_solr[q]',
      label,
      suggestionItems,
      formProps,
      hideSubmit,
      tag: Tag = 'form',
      value: presetValue,
      submitButtonProps,
      suggestTitle,
    } = props;
    const [inputItems, setInputItems] = React.useState<string[]>([]);
    const [error, setError] = React.useState<?string>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [isKeyboardNavigation, setIsKeyboardNavigation] =
      React.useState<boolean>(false);
    const internalRef = useRef<?HTMLInputElement>(null);

    // Merge internal ref with external ref
    const setRefs = React.useCallback(
      (element: ?HTMLInputElement) => {
        internalRef.current = element;
        if (typeof ref === 'function') {
          // $FlowFixMe
          ref(element);
        } else if (ref) {
          // $FlowFixMe
          ref.current = element;
        }
      },
      [ref]
    );

    const onInputValueChange = useCallback(
      async (
        downshiftInput: { inputValue?: string, selectedItem?: string | null },
        selectedItem?: string | null
      ) => {
        const inputValue = downshiftInput.inputValue ?? '';
        if (
          inputValue.length >= 2 &&
          inputValue !==
            (downshiftInput.selectedItem
              ? downshiftInput.selectedItem
              : 'empty')
        ) {
          setLoading(true);
          setError(null);
          try {
            const items = await fetchAutocomplete(
              decodeURIComponent(
                `${suggestUrl}&${suggestFieldName}=${inputValue}`
              )
            );
            if (items.length === 0) {
              setError('no results');
            }
            setInputItems(items);
          } catch (err) {
            console.error('Error fetching autocomplete suggestions:', err);
            setError('search failed');
          } finally {
            setLoading(false);
          }
        }
      },
      [suggestUrl, suggestFieldName]
    );
    const debouncedInputValueChange = useDebounce(onInputValueChange, 450);
    const {
      selectItem,
      inputValue: value,
      isOpen,
      getLabelProps,
      getMenuProps,
      getInputProps,
      highlightedIndex,
      getItemProps,
      reset,
      closeMenu,
      inputValue,
    } = useCombobox({
      initialInputValue: presetValue || '',
      items: inputItems,
      onInputValueChange: debouncedInputValueChange,
      onHighlightedIndexChange: ({ type }) => {
        // Check if the change was caused by keyboard navigation
        const keyboardTypes = [
          useCombobox.stateChangeTypes.InputKeyDownArrowDown,
          useCombobox.stateChangeTypes.InputKeyDownArrowUp,
          useCombobox.stateChangeTypes.InputKeyDownHome,
          useCombobox.stateChangeTypes.InputKeyDownEnd,
        ];
        setIsKeyboardNavigation(keyboardTypes.includes(type));
      },
    });

    const handleFocus = (e: any) => {
      e.target.select();
    };

    const handleClear = () => {
      reset();
      closeMenu();
      setError(null);
      setLoading(false);
      // Focus the input - handle both object and internal refs
      if (internalRef.current) {
        internalRef.current.focus();
      }
    };

    /** Prevent Downshift's default 'Enter' behavior, when no item is highlighted. This is necessary to allow submitting the form via Enter when searching for something that is not in the suggestions. */
    const handleKeyDown = (event: any) => {
      /** Make the events not bubble as e.g. Modals also react to esc key */
      if (inputValue.length !== 0) {
        event.stopPropagation();
      }

      /** Prevent Downshift's default 'Enter' behavior, when no item is highlighted. This is necessary to allow submitting the form via Enter when searching for something that is not in the suggestions. */
      if (event.key === 'Enter') {
        if (highlightedIndex === -1) {
          // $FlowFixMe: allow setting custom flag on nativeEvent to prevent Downshift default behavior
          (event.nativeEvent: any).preventDownshiftDefault = true;
        }
      }
    };

    const attr = Tag === 'form' ? { role: 'search', ...formProps } : {};

    // Trigger loading animation immediately when inputValue changes outside of its debounce function
    useEffect(() => {
      if (isOpen && inputValue.length > 0) {
        setLoading(true);
      } else {
        setLoading(false);
      }
    }, [isOpen, inputValue]);

    return (
      <Tag className="search" {...attr}>
        <div className="search__inner">
          <div className="form-group form-group--large">
            <Label {...getLabelProps()}>{label}</Label>
            <div className="search__wrap">
              <Input
                {...getInputProps({
                  placeholder,
                  name,
                  onFocus: handleFocus,
                  onKeyDown: handleKeyDown,
                  ref: setRefs,
                  inputMode: 'search',
                  'aria-expanded': inputItems.length > 0 && isOpen,
                })}
                classNames="input--large"
              />
              {value && value.length > 0 && (
                <Button
                  icon="close"
                  style="icon"
                  onClick={handleClear}
                  className="search__close"
                  ariaLabel={t('Eingabe löschen')}
                />
              )}
            </div>
            <div
              className={classNames(
                !(isOpen && (loading || error)) && 'u-hidden-visually'
              )}
              aria-live="polite"
              aria-atomic="true"
            >
              {isOpen && loading && <>{t('Vorschläge werden geladen')}...</>}
              {isOpen && error && (
                <>{t(`Es konnten keine Ergebnisse gefunden werden.`)}</>
              )}
            </div>
            <ul
              className={classNames(
                'search__dropdown dropdown-menu__list',
                isOpen && 'dropdown-menu search__dropdown--open',
                !(isOpen && (inputValue.length > 0 || error)) && 'hidden'
              )}
              {...getMenuProps()}
            >
              {isOpen && error && (
                <li className="dropdown-menu__list-item">
                  <div className="dropdown-menu__link search__error">
                    {t(`Es konnten keine Ergebnisse gefunden werden.`)}
                  </div>
                </li>
              )}
              {isOpen && loading && !error && (
                <>
                  <li className="dropdown-menu__list-item">
                    <div
                      className="dropdown-menu__link search__item search__loader"
                      aria-hidden="true"
                    >
                      <span className="u-hidden-visually">
                        {t('Vorschläge werden geladen')}...
                      </span>
                    </div>
                  </li>
                  <li className="dropdown-menu__list-item">
                    <div
                      className="dropdown-menu__link search__item search__loader"
                      aria-hidden="true"
                    ></div>
                  </li>
                </>
              )}
              {!loading &&
                !error &&
                isOpen &&
                inputItems.length > 0 &&
                inputItems.map((item, index) => {
                  const isHighlighted = highlightedIndex === index;
                  const listItemClassName = classNames(
                    'dropdown-menu__list-item',
                    'search__item',
                    isHighlighted && 'dropdown-menu__list-item--active',
                    isHighlighted &&
                      isKeyboardNavigation &&
                      'search__item--keyboard-focus',
                    isHighlighted &&
                      !isKeyboardNavigation &&
                      'search__item--hover'
                  );
                  return (
                    <li
                      className={listItemClassName}
                      key={`${item}${index}`}
                      {...getItemProps({ item, index })}
                    >
                      <span className="dropdown-menu__link">
                        <span className="dropdown-menu__text">{item}</span>
                      </span>
                    </li>
                  );
                })}
            </ul>
          </div>

          {!hideSubmit && (
            <Button
              type="submit"
              icon="search"
              style="icon"
              iconTitle={
                submitButtonProps?.children ? undefined : t('Suche abschicken')
              }
              className="button--large search__submit"
              {...submitButtonProps}
            />
          )}
        </div>
        {suggestionItems && suggestionItems.length > 0 && (
          <div className="search__bottom">
            <h2 className="search__headline">
              {suggestTitle || t('Top-Themen')}
            </h2>
            <ul className="search__list">
              {suggestionItems.map((item, index) => (
                <li className="search__list-item" key={`${item}${index}`}>
                  <a
                    className="button button--primary button--small"
                    onClick={() => selectItem(item)}
                    href={`${formProps?.action || '/'}?${name}=${item || ''}`}
                  >
                    <span className="button__text">{item}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Tag>
    );
  }
);

Search.displayName = 'Search';

export default Search;
